import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  GROUP_META,
  GROUP_ORDER,
  groupIdFor,
  labelFor,
  menuKey,
} from "../config/settingsTaxonomy";

// Flattens the hierarchical settings menu the API returns into leaf items.
//
// GetMenuHierarchical returns top-level SettingsMenu rows, most carrying a subMenu[] of
// SettingsInnerMenu rows. A few top-level rows (Group Video Training, USDA Food Data Central)
// have no subMenu and are links in their own right, so they count as leaves too.
const flattenMenu = (menuList) => {
  const leaves = [];
  const seen = new Set();

  const push = (item, parentName) => {
    const key = menuKey(item);
    // Inactive placeholder rows all collapse to menuUrl "/" with no usable target.
    if (!key || key === "name:") return;
    if (seen.has(key)) return;
    seen.add(key);
    leaves.push({
      key,
      menuName: item.menuName?.trim() || "",
      menuUrl: item.menuUrl,
      label: labelFor(key, item.menuName),
      parentName: parentName || item.menuName?.trim() || "",
      groupId: groupIdFor(key),
      raw: item,
    });
  };

  (menuList || []).forEach((top) => {
    if (Array.isArray(top.subMenu) && top.subMenu.length > 0) {
      top.subMenu.forEach((child) => push(child, top.menuName?.trim()));
    } else {
      push(top, top.menuName?.trim());
    }
  });

  return leaves;
};

const normalize = (s) => (s || "").toLowerCase().trim();

// Strips spaces and punctuation so "Course Lable" matches the courseSetting/courseLable url and
// "SMTP" matches "smtpConfig". Several menu urls carry intentional typos that admins have
// learnt, and they should not have to reproduce the spacing exactly.
const squash = (s) => normalize(s).replace(/[^a-z0-9]/g, "");

/**
 * Single source of navigation truth for the three settings surfaces (hub, top bar, sidebar).
 *
 * Only ever regroups what MenuList already contains, so a user can never see an item the
 * access API did not return for them.
 */
export default function useSettingsNav() {
  const MenuList = useSelector((state) => state.setting?.MenuList);

  return useMemo(() => {
    const flatItems = flattenMenu(MenuList);

    const byGroup = flatItems.reduce((acc, item) => {
      (acc[item.groupId] = acc[item.groupId] || []).push(item);
      return acc;
    }, {});

    // Only groups the user actually has items in, in the taxonomy's declared order.
    const groups = GROUP_ORDER.filter((id) => byGroup[id]?.length).map((id) => ({
      id,
      ...GROUP_META[id],
      items: byGroup[id],
    }));

    // Matches on the display label, the original DB name and the url, so someone who knows
    // the old name ("Course Lable") still finds the renamed item.
    const search = (query) => {
      const q = normalize(query);
      if (!q) return flatItems;
      const sq = squash(query);
      return flatItems.filter(
        (i) =>
          normalize(i.label).includes(q) ||
          normalize(i.menuName).includes(q) ||
          normalize(i.parentName).includes(q) ||
          (!!sq &&
            (squash(i.menuUrl).includes(sq) ||
              squash(i.label).includes(sq) ||
              squash(i.menuName).includes(sq)))
      );
    };

    const findByUrl = (url) => {
      const target = normalize(url).replace(/^\/settings\//, "");
      if (!target) return null;
      return (
        flatItems.find((i) => normalize(i.menuUrl) === target) ||
        // Route params only carry the last segment (e.g. "user" from "hrSetting/user").
        flatItems.find((i) => normalize(i.menuUrl).split("/").pop() === target) ||
        null
      );
    };

    return { groups, flatItems, search, findByUrl };
  }, [MenuList]);
}
